import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LayoutDashboard, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { navItemForPath } from "@/lib/apps";
import { FOLDED_BY_DEFAULT, buildSections, makeVisible, sectionOf } from "@/lib/navModel";

// v2: the sections were reordered by daily use; weekly ones (reports, people, setup) start folded.
const OPEN_KEY = "madio.nav.closed.v2";
const readClosed = () => {
  try {
    const saved = window.localStorage.getItem(OPEN_KEY);
    return saved === null ? [...FOLDED_BY_DEFAULT] : JSON.parse(saved);
  } catch { return [...FOLDED_BY_DEFAULT]; }
};
const writeClosed = (v) => { try { window.localStorage.setItem(OPEN_KEY, JSON.stringify(v)); } catch { /* a per-browser convenience only */ } };

/** Grouped left navigation. `collapsed` shows icons only (labels become tooltips); a group can be folded, the group of the
 * page you are on never stays folded. Only pages the person may open are listed (same rule as every other shell). */
export default function ModernSidebar({ collapsed = false, onNavigate, brand }) {
  const { pathname } = useLocation();
  const { user, canAccess, canDo, canSeeCost } = useAuth();
  const [closed, setClosed] = useState(readClosed);
  const sections = useMemo(() => buildSections(makeVisible({ user, canAccess, canSeeCost })), [user, canAccess, canSeeCost]);
  const current = navItemForPath(pathname);
  const currentSection = current ? sectionOf(current) : "";

  useEffect(() => { writeClosed(closed); }, [closed]);
  const canQuickAdd = Boolean(canDo?.("leads", "create"));          // the same right the Leads page needs to add one
  const toggle = (id) => setClosed((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <nav aria-label="Main" className={`h-full flex flex-col bg-[var(--color-surface)] border-r border-[var(--color-border)] transition-[width] duration-200 ${collapsed ? "w-[68px]" : "w-64"}`} data-testid="modern-sidebar">
      {brand}
      {canQuickAdd && (
        <div className={`px-2 pt-3 ${collapsed ? "flex justify-center" : ""}`}>
          <Link to="/leads?quick=1" onClick={onNavigate} data-testid="modern-quick-add-lead" title={collapsed ? "Quick add lead" : undefined}
                aria-label={collapsed ? "Quick add lead" : undefined}
                className={`flex items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-primary)] text-white font-semibold text-sm min-h-[40px] hover:bg-[var(--color-primary-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)] ${collapsed ? "w-10" : "w-full px-3"}`}>
            <Plus size={16} aria-hidden="true" />{!collapsed && "Quick add lead"}
          </Link>
        </div>
      )}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-1">
        {sections.map((section) => {
          const folded = !collapsed && closed.includes(section.id) && section.id !== currentSection;
          const panel = `nav-${section.id}`;
          return (
            <div key={section.id} className="pb-1">
              {collapsed ? <div className="mx-3 my-2 border-t border-[var(--color-border)] first:hidden" role="presentation" /> : (
                <button type="button" onClick={() => toggle(section.id)} aria-expanded={!folded} aria-controls={panel}
                        className="touch-target w-full flex items-center gap-2 justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] hover:text-[var(--color-text)] rounded text-left">
                  {/* flex-1 min-w-0 + text-left: a label long enough to wrap (e.g. "Architect Studio &
                      Catalogue") otherwise inherits the browser's default centered button text-align,
                      which is invisible on every one-line label and only shows up as mis-centered wrapped
                      text here. */}
                  <span className={`flex-1 min-w-0 text-left ${section.id === currentSection ? "text-[var(--color-primary-hover)]" : ""}`} data-current-section={section.id === currentSection ? section.id : undefined}>{section.label}</span>
                  <ChevronDown size={14} className={`shrink-0 transition-transform ${folded ? "-rotate-90" : ""}`} aria-hidden="true" />
                </button>
              )}
              <ul id={panel} hidden={folded} className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon || LayoutDashboard;
                  const active = current?.id === item.id;
                  return (
                    <li key={item.id}>
                      <Link to={item.to} onClick={onNavigate} title={collapsed ? item.label : undefined} aria-current={active ? "page" : undefined}
                            aria-label={collapsed ? item.label : undefined} data-testid={`modern-nav-${item.id}`}
                            className={`touch-target group relative flex items-center gap-3 rounded-[var(--radius-md)] px-3 min-h-[40px] text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-[var(--color-primary)] ${collapsed ? "justify-center" : ""} ${
                              active ? "bg-[var(--color-primary-soft)] text-[var(--color-primary-hover)]"
                                : "text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"}`}>
                        {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-[var(--color-primary)]" aria-hidden="true" />}
                        <Icon size={18} strokeWidth={active ? 2.25 : 1.75} className="shrink-0" aria-hidden="true" />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
